import { useRef, useState, useEffect, useMemo } from "react";
import { CursorType } from "../cursor-type";
import styles from "./side-menu.module.css";
import { useBoardValue } from "frontend/state/board-state";
import { useAtom, useSetAtom } from "jotai";
import { TipName } from "shared/datamodel/schemas";
import WalkthroughTipPresenter from "frontend/walkthrough-tips/walkthrough-tip-presenter";
import { useNextWalkthroughTip } from "frontend/walkthrough-tips/use-walkthrough-tip";
import { MenuItem } from "./side-menu-items/menu-item";
import SubMenu from "../sub-menu";
import SideMenuOverflow from "./side-menu-overlow/side-menu-overflow";
import { submenuDataAtom } from "state-atoms";
import useStateValue from "frontend/state/value";
import { InstanceType } from "shared/datamodel/schemas/ability";
import useAbility from "frontend/hooks/use-ability";
import { signUpToContinueModalAtom } from "state-atoms/modal-atoms";
import * as items from "./side-menu-items/side-menu-items";
import { useSideMenuLayout } from "./hooks/use-side-menu-overflow";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { useShowMondayFeatures } from "frontend/canvas-page/hooks/use-show-monday-features";
import { getMenuItems } from "./side-menu-items/side-menu-items";
import { CanvasSideMenuProps } from "./side-menu";

export function CanvasSideMenuStandard({
  currentCursorType,
  onChangeCursorType,
  setShowSidePanel,
  toggleShowIntegrations,
  showSubmenu,
  setShowSubmenu,
  setCursorType,
  showSideMenuOverflow,
  setShowSideMenuOverflow,
  setShowRestrictedModal,
}: Omit<CanvasSideMenuProps, "isCimReorderEnabled">) {
  // Atoms
  const [submenuData, setSubmenuData] = useAtom(submenuDataAtom);
  const setShowSignUpToContinueModal = useSetAtom(signUpToContinueModalAtom);

  // Flags
  const showMondayFeatures = useShowMondayFeatures();
  const newTaskEnabled = useFeatureFlag("new-task-element");

  // State
  const [{ config: boardConfig, user, board }, dispatch] = useBoardValue();
  const { nextTip } = useNextWalkthroughTip(board ?? null);
  const [globalState] = useStateValue();
  const { canPerformAnyAction } = useAbility();

  const [refToCursor, setRefToCursor] = useState(null);

  const menuItems = useMemo(
    () => getMenuItems(newTaskEnabled, showMondayFeatures),
    [newTaskEnabled, showMondayFeatures]
  );

  // Menu layout
  const menuRef = useRef<HTMLDivElement>(null);
  const [mainSideMenuItems, setMainSideMenuItems] = useState<items.MenuItem[]>(menuItems);
  const [sideMenuOverflowItems, setSideMenuOverflowItems] = useState<items.MenuItem[]>([]);
  const [recentlyUsedItem, setRecentlyUsedItem] = useState<items.MenuItem | null>(null);
  const recentlyUsedItemRef = useRef<HTMLDivElement>(null);
  const sideMenuOverflowRef = useRef<HTMLDivElement>(null);
  const threeDotsRef = useRef<HTMLDivElement>(null);

  const sliceIndex = useSideMenuLayout(menuItems.length, !!recentlyUsedItem);

  // Set menu items based on layout
  useEffect(() => {
    let filteredMenuItems = menuItems.filter((item) => {
      if (item.type === CursorType.mondayIntegration) {
        return showMondayFeatures;
      }

      return item.type !== recentlyUsedItem?.type;
    });

    if (sliceIndex === -1) {
      // Minimum screen size
      setRecentlyUsedItem(recentlyUsedItem || menuItems[0]);
      setMainSideMenuItems([]);
      setSideMenuOverflowItems(filteredMenuItems);
      return;
    }

    if (sliceIndex === menuItems.length) {
      // No overflow:
      setMainSideMenuItems(menuItems);
      setSideMenuOverflowItems([]);
      setShowSideMenuOverflow(false);
      if (currentCursorType === CursorType.sideMenuOverflow) {
        onChangeCursorType(CursorType.pointer);
      }
      return;
    }

    filteredMenuItems = filteredMenuItems.filter((item) => item.type !== CursorType.mondayIntegration);
    setMainSideMenuItems(filteredMenuItems.slice(0, sliceIndex));
    setSideMenuOverflowItems(filteredMenuItems.slice(sliceIndex));

    // TODO: This had an array inside the depts from the start.
    // Keeping it, but it makes it so that the hook is called on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliceIndex, menuItems, recentlyUsedItem, newTaskEnabled, showMondayFeatures]);

  useEffect(() => {
    if (!nextTip) return;
    const item = sideMenuOverflowItems.find((item) => item.tipName === nextTip);
    if (item) {
      setRecentlyUsedItem(item);
    }
  }, [nextTip, sideMenuOverflowItems]);

  function onItemClick(item: items.MenuItem, ref: any = null, isSideMenuOverflow: boolean = false) {
    if (globalState.user && board && !canPerformAnyAction(globalState.user, board, InstanceType.Board)) {
      setShowRestrictedModal(true);
      return;
    }

    if (item.type === CursorType.mondayIntegration) {
      if (user?.isAnonymous) {
        setShowSignUpToContinueModal(true);
        return;
      }

      toggleShowIntegrations();
      return;
    }

    if (isSideMenuOverflow) {
      ref = recentlyUsedItemRef;
    }
    const validTypes = [CursorType.move, CursorType.pointer];
    const signupOnlyItems = [CursorType.comment, CursorType.template];
    const showSignupToAnonymousUser = user?.isAnonymous && signupOnlyItems.includes(item.type);
    if (showSignupToAnonymousUser) {
      setShowSignUpToContinueModal(true);
      return;
    }
    if (boardConfig.isViewOnly && !validTypes.includes(item.type)) {
      dispatch({ type: "show_signup", payload: true });
    } else {
      if (item.type === CursorType.sideMenuOverflow) {
        setShowSideMenuOverflow(true);
      }

      if (
        !mainSideMenuItems.some((mainItem) => mainItem.type === item.type) &&
        item.type !== CursorType.sideMenuOverflow
      ) {
        setRecentlyUsedItem({ ...item });
      }

      // Add option to deselect the pointer, and switch to move cursor.
      if (item.type === CursorType.pointer && currentCursorType === CursorType.pointer) {
        setRefToCursor(ref);
        onChangeCursorType(CursorType.move);
        return;
      }

      setRefToCursor(ref);
      onChangeCursorType(item.type);
    }
  }

  const addElementsTip = nextTip === TipName.walkthroughAddElement;

  function renderMenuItem(item: items.MenuItem, ref: any = null, isSideMenuOverflow: boolean = false) {
    const isHighlighted = item.isHighlighted ? item.isHighlighted(currentCursorType) : currentCursorType === item.type;
    return (
      <MenuItem
        key={item.type}
        item={item}
        onItemClick={onItemClick}
        highlight={isHighlighted}
        addElementsTip={addElementsTip}
        nextTip={nextTip}
        setShowSidePanel={setShowSidePanel}
        relativeTo={ref}
        isSideMenuOverflow={isSideMenuOverflow}
      />
    );
  }

  return (
    <>
      <div data-testid="sidemenu" id="tools-sidemenu" className={styles.container} ref={menuRef}>
        <div className={styles.itemsContainer}>{mainSideMenuItems.map((item) => renderMenuItem(item))}</div>

        {showSideMenuOverflow && (
          <SideMenuOverflow
            menuItems={sideMenuOverflowItems}
            renderMenuItem={renderMenuItem}
            relativeTo={sideMenuOverflowRef}
          />
        )}

        {showSubmenu && refToCursor && (
          <SubMenu
            showSubmenu={showSubmenu}
            cursorType={currentCursorType}
            setShowSubmenu={setShowSubmenu}
            setSubmenuData={setSubmenuData}
            submenuData={submenuData}
            setCursorType={setCursorType}
            refToCursor={refToCursor}
            isCimReorderEnabled={false}
          />
        )}

        {sideMenuOverflowItems.length > 0 && (
          <>
            {recentlyUsedItem && (
              <div ref={recentlyUsedItemRef}>{renderMenuItem(recentlyUsedItem, recentlyUsedItemRef)}</div>
            )}
            <div ref={sideMenuOverflowRef}>
              {showMondayFeatures &&
                renderMenuItem({
                  ...items.mondayIntegrationMenuItem,
                  isHighlighted: (cursor: CursorType) => cursor === CursorType.mondayIntegration,
                })}
              {renderMenuItem(items.threeDotsMenuItem, threeDotsRef)}
            </div>
          </>
        )}

        <WalkthroughTipPresenter tipName={TipName.walkthroughAddElement} attachToRef={menuRef} side="right" />
      </div>
    </>
  );
}
